import { Typography, Button } from 'antd';
import { format } from 'date-fns';
import FileSaver from 'file-saver';
import * as clonedeep from 'lodash.clonedeep';
import React, { useState, useEffect } from 'react';

import GachaWinningTable from '@/GachaWinning/components/GachaWinningTable';
import { GachaWinning } from '@/api-client/generated/models';
import { useGacha } from '@/hooks/context/GachaContext';
import { useGiftCodeCrypto } from '@/hooks/context/GiftCodeCryptContext';
import { createApiClientWithToken } from '@/service/PreRegistrationApiFactory';

const dateFormat = 'yyyy/MM/dd HH:mm:ss';

const ListGachaWinning: React.FC<{}> = () => {
  const api = createApiClientWithToken();
  const [gachaWinnings, setGachaWinnings] = useState<GachaWinning[]>([]);
  const [gachaWinningsNum, setGachaWinningsNum] = useState<number>(0);
  const [os, setOs] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage] = useState<number>(50); // 設定する必要があるかわからんので 50 でいったん固定（サーバー側も）
  const [searchWord, setSearchWord] = useState<string>('');
  const { gacha } = useGacha();
  const { giftCodeCrypto } = useGiftCodeCrypto();

  const decryptGiftCodes = (_gachaWinnings: GachaWinning[]) =>
    _gachaWinnings.map((gachaWinning) => {
      const newGachaWinning: GachaWinning = clonedeep(gachaWinning);
      newGachaWinning.giftCode = giftCodeCrypto.decryptGiftCode(newGachaWinning.giftCode);
      return newGachaWinning;
    });

  const listGachaWinning = async (
    status: string,
    _perPage: number,
    offset: number,
    _os: string,
    isSmsAuthedUser: boolean,
    _searchWord: string
  ) =>
    api.listGachaWinning(gacha.id, {
      limit: _perPage,
      offset,
      status,
      os: _os,
      isSmsAuthedUser,
      searchWord: _searchWord,
    });

  useEffect(() => {
    listGachaWinning('', perPage, 0, os, false, searchWord).then((response) => {
      setGachaWinnings(decryptGiftCodes(response.winnings || []));
      setGachaWinningsNum(response.totalNum);
    });
  }, []);

  const onChangeCurrentPage = async (newPage: number) => {
    const response = await listGachaWinning('', perPage, (newPage - 1) * perPage, os, false, searchWord); // index 調整のため newPage - 1

    setCurrentPage(newPage);
    setGachaWinnings(response.winnings || []);
  };

  const onChangeOs = async (_os: string) => {
    const response = await listGachaWinning('', perPage, 0, _os, false, searchWord);

    setOs(_os);
    setCurrentPage(1);
    setGachaWinnings(response.winnings || []);
    setGachaWinningsNum(response.totalNum);
  };

  const onSearch = async (word: string) => {
    setSearchWord(word);
    const response = await listGachaWinning('', perPage, 0, os, false, word);

    setGachaWinnings(response.winnings);
    setCurrentPage(1);
    setGachaWinningsNum(response.totalNum);
  };

  const exportCsv = async () => {
    const response = await api.listAllGachaWinning(gacha.id);

    const giftCodeStatusValue = {
      unauthed: 'SMS 認証待ち',
      unapproved: '承認待ち',
      gifted: '配信済み',
      rejected: '棄却済み',
    };

    const headers = ['ユーザ名', 'メールアドレス', 'OS', '当選日', '金額', 'ギフトコード配信ステータス'];
    const body = response.winnings.map((winning) => [
      winning.user.nickname,
      winning.user.mailAddress,
      winning.os,
      format(winning.createdAt, dateFormat),
      winning.giftCode.price,
      giftCodeStatusValue[winning.status] || 'ステータス取得でエラーが発生しました',
    ]);
    const csvRows = [headers, ...body];
    const csv = csvRows.map((row) => row.join(',')).join('\r\n');

    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([bom, csv], { type: 'text/csv' });
    FileSaver.saveAs(blob, `${gacha.name}-当選者一覧-${format(Date.now(), dateFormat)}.csv`);
  };

  return (
    <div>
      <Typography.Title>当選者一覧</Typography.Title>
      <Button onClick={exportCsv}>CSVエクスポート</Button>
      <GachaWinningTable
        gachaWinnings={gachaWinnings}
        gachaWinningsNum={gachaWinningsNum}
        currentPage={currentPage}
        perPage={perPage}
        onChangeCurrentPage={onChangeCurrentPage}
        onChangeOs={onChangeOs}
        onSearch={onSearch}
      />
    </div>
  );
};

export default ListGachaWinning;
